import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, qs, qsa, onClick, toast } from '../dom.js';
import { icon } from '../icons.js';
import { validateExam, exportExam, TestifyApiError } from '../../testify/api.js';
import { applyExportedExam, subjectOptions, type ImportResult } from '../../testify/import.js';
import { parseTestifyLink } from '../../testify/link.js';
import { PRESETS } from '../../db/schema.js';
import { scanQrDialog } from '../qrScan.js';
import type { TestifyValidateResponse, TestifyExportResponse } from '../../testify/types.js';

// Import-from-TESTIFY flow (brief §13.5), parallel to Create Quiz. Scan the exam
// QR (or paste its link) to fill exam code + token → /validate → confirm →
// /export → store one LOCKED quiz per compatible subject. Typing the bearer token
// by hand is the fallback, not the happy path. Upload side NOT built yet (§13.8).
export async function testifyImportView(): Promise<RenderedView> {
  return {
    title: 'Import from TESTIFY',
    back: '/',
    html: `<div class="view">
      <div class="card"><div class="stack">
        <button class="btn btn-primary btn-block" data-qr>${icon.camera()} Scan exam QR code</button>
        <button class="btn btn-ghost btn-block" data-paste>${icon.doc()} Paste exam link</button>
        <p class="hint" style="margin:0">The QR on your exam-school sheet carries both the exam code and the token.</p>
      </div></div>
      <p class="section-label">Or enter them by hand</p>
      <div class="card"><div class="stack">
        <div class="field"><label for="exam">Exam code</label><input class="input" id="exam" autocomplete="off" placeholder="Type the exam code given by TESTIFY"></div>
        <div class="field" style="margin:0"><label for="token">Exam-school token</label><input class="input mono" id="token" autocomplete="off" placeholder="Paste your school's token for this exam"></div>
        <button class="btn btn-primary btn-block" data-validate>${icon.doc()} Validate</button>
      </div></div>
      <p class="hint">TESTIFY re-scores every upload server-side — GRADIFY's local score is advisory. The answer key arrives locked from TESTIFY and can't be edited here.</p>
      <div id="out" style="margin-top:14px"></div>
    </div>`,
    mount(root) {
      const out = qs<HTMLDivElement>(root, '#out')!;

      const showConfirm = (examCode: string, token: string, v: TestifyValidateResponse) => {
        out.innerHTML = `<div class="card"><div class="stack">
          <div><strong>${esc(v.school.school_name)}</strong>${v.school.school_code ? ` <span class="badge badge-muted">${esc(v.school.school_code)}</span>` : ''}</div>
          <div class="muted" style="font-size:14px">${esc(v.exam.title)} · ${esc(v.exam.grade_level)} · compile v${esc(String(v.exam.compile_version))}</div>
          <div class="muted" style="font-size:14px">${v.subjects.length} subject(s) on this exam</div>
          <button class="btn btn-primary btn-block" data-fetch>${icon.check()} Choose subjects to import</button>
          <p class="hint" style="margin:0">A unified exam carries every subject in the region. You pick the ones your school actually offers.</p>
        </div></div>`;
        onClick(out, '[data-fetch]', async () => {
          out.innerHTML = `<div class="center-col"><div class="spinner"></div><p>Fetching subjects…</p></div>`;
          try {
            showPicker(examCode, token, v, await exportExam(examCode, token));
          } catch (e) {
            showError(e);
          }
        });
      };

      /**
       * Subject picker. A unified exam can offer a hundred subjects while a school
       * teaches a handful, so importing everything buries the teacher's own quizzes.
       *
       * Nothing is ticked by default: on a 100-subject exam a pre-ticked list makes
       * "import everything" the accidental one-tap outcome, which is the problem this
       * screen exists to solve. "Select all" is one tap away for a small exam.
       */
      const showPicker = (examCode: string, token: string, v: TestifyValidateResponse, exp: TestifyExportResponse) => {
        const options = subjectOptions(exp);
        const offered = options.filter((o) => o.importable);
        const blocked = options.filter((o) => !o.importable);
        const selected = new Set<string>();

        const rows = offered
          .map(
            (o) => `<label class="rp-pick pick-row" data-title="${esc(o.title.toLowerCase())}">
              <input type="checkbox" data-pick="${esc(o.testId)}">
              <span><strong>${esc(o.title)}</strong><span class="meta muted">${o.items} items${o.competencies ? ` · ${o.competencies} competenc${o.competencies === 1 ? 'y' : 'ies'}` : ''}</span></span>
            </label>`,
          )
          .join('');

        // Grouped by reason: 56 rows of "Subject has no questions." is noise, one
        // line saying 56 subjects have none is information.
        const byReason = new Map<string, string[]>();
        for (const b of blocked) {
          const list = byReason.get(b.reason ?? '');
          if (list) list.push(`${b.title} (${b.items})`);
          else byReason.set(b.reason ?? '', [`${b.title} (${b.items})`]);
        }
        const blockedBlock = blocked.length
          ? `<details class="disclose"><summary>${blocked.length} subject(s) cannot be imported</summary>
              <ul class="import-log">${[...byReason.entries()]
                .map(([reason, list]) => `<li class="warn"><strong>${esc(reason)}</strong> — ${esc(list.join(', '))}</li>`)
                .join('')}</ul>
              <p class="hint">Item counts must match a printed sheet (${PRESETS.join(', ')} items). Subjects with no questions have to be completed in TESTIFY first.</p>
            </details>`
          : '';

        out.innerHTML = `<div class="card"><div class="stack">
          <div><strong>${esc(v.school.school_name)}</strong>${v.school.school_code ? ` <span class="badge badge-muted">${esc(v.school.school_code)}</span>` : ''}</div>
          <div class="muted" style="font-size:14px">${esc(exp.exam.title ?? v.exam.title)} · compile v${esc(String(exp.exam.compile_version))}</div>
          ${offered.length
            ? `<div class="field" style="margin:0"><input class="input" id="pick-filter" autocomplete="off" placeholder="Filter ${offered.length} subjects by name…"></div>
               <div class="row2"><button class="btn btn-ghost btn-sm" data-all>Select all</button><button class="btn btn-ghost btn-sm" data-clear>Clear</button></div>
               <div class="pick-list">${rows}</div>
               <p class="pick-count muted" style="font-size:13px;margin:0"></p>
               <button class="btn btn-primary btn-block" data-import disabled>${icon.check()} Import selected</button>
               <p class="hint" style="margin:0">Each subject becomes its own locked quiz. You can come back and import more later.</p>`
            : `<span class="badge badge-warn">${icon.alert()} No subject on this exam can be imported yet.</span>`}
          ${blockedBlock}
        </div></div>`;

        const importBtn = qs<HTMLButtonElement>(out, '[data-import]');
        const countEl = qs<HTMLParagraphElement>(out, '.pick-count');
        const boxes = qsa<HTMLInputElement>(out, '[data-pick]');
        const visibleBoxes = () => boxes.filter((b) => !b.closest<HTMLElement>('.pick-row')!.hidden);

        const refresh = () => {
          if (!importBtn || !countEl) return;
          importBtn.disabled = selected.size === 0;
          importBtn.innerHTML = selected.size
            ? `${icon.check()} Import ${selected.size} subject${selected.size === 1 ? '' : 's'}`
            : `${icon.check()} Import selected`;
          countEl.textContent = `${selected.size} of ${offered.length} selected`;
        };

        boxes.forEach((box) =>
          box.addEventListener('change', () => {
            const id = box.dataset.pick!;
            if (box.checked) selected.add(id);
            else selected.delete(id);
            refresh();
          }),
        );
        // All / Clear act on what is visible, so they compose with the filter
        // instead of silently overriding it.
        const setVisible = (on: boolean) =>
          visibleBoxes().forEach((b) => {
            b.checked = on;
            if (on) selected.add(b.dataset.pick!);
            else selected.delete(b.dataset.pick!);
          });
        onClick(out, '[data-all]', () => {
          setVisible(true);
          refresh();
        });
        onClick(out, '[data-clear]', () => {
          setVisible(false);
          refresh();
        });
        qs<HTMLInputElement>(out, '#pick-filter')?.addEventListener('input', (e) => {
          const q = (e.target as HTMLInputElement).value.trim().toLowerCase();
          qsa<HTMLElement>(out, '.pick-row').forEach((row) => {
            row.hidden = !!q && !(row.dataset.title ?? '').includes(q);
          });
        });
        refresh();

        onClick(out, '[data-import]', async () => {
          if (!selected.size) return;
          out.innerHTML = `<div class="center-col"><div class="spinner"></div><p>Importing ${selected.size} subject(s)…</p></div>`;
          try {
            showResult(await applyExportedExam(repos, examCode, token, v, exp, { selectedTestIds: [...selected] }));
          } catch (e) {
            showError(e);
          }
        });
      };

      const showResult = (res: ImportResult) => {
        const createdList = res.created.length
          ? `<ul class="list">${res.created.map((c) => `<li class="row" style="cursor:default"><span class="grow"><span class="ttl">${esc(c.title)}</span><span class="meta">${c.items} items · locked key</span></span></li>`).join('')}</ul>`
          : `<p class="hint">No compatible subjects were imported.</p>`;
        const incompat = res.incompatible.length
          ? `<p class="section-label">Not imported</p><ul class="import-log">${res.incompatible
              .map((s) => `<li class="warn">${esc(s.name)} (${s.items} items) — ${esc(s.reason)}</li>`)
              .join('')}</ul>`
          : '';
        const skipped = res.notSelected.length
          ? `<p class="hint">${res.notSelected.length} other subject(s) on this exam were not selected. Import from TESTIFY again to add any of them.</p>`
          : '';
        const cf = (res.requiredCustomFields ?? []).filter((f) => f.required);
        const cfNote = cf.length
          ? `<p class="hint">${icon.alert()} This exam requires ${cf.map((f) => esc(f.label)).join(', ')} — you'll set ${cf.length > 1 ? 'these' : 'that'} once per class when you assign it.</p>`
          : '';
        out.innerHTML = `<div class="card"><div class="stack">
          <div><strong>${res.created.length}</strong> subject(s) imported from <strong>${esc(res.examTitle || res.examCode)}</strong> for ${esc(res.schoolName)}.</div>
          <div class="muted" style="font-size:13px">Each subject becomes its own quiz, tagged as part of this unified exam.</div>
          ${createdList}${skipped}${incompat}${cfNote}
          <button class="btn btn-primary btn-block" data-done>${icon.check()} Done — go to quizzes</button>
        </div></div>`;
        onClick(out, '[data-done]', () => navigate('#/'));
      };

      const showError = (e: unknown) => {
        const msg = e instanceof TestifyApiError ? e.message : e instanceof Error ? e.message : String(e);
        out.innerHTML = `<div class="card"><span class="badge badge-danger">${icon.alert()} ${esc(msg)}</span></div>`;
      };

      const examEl = qs<HTMLInputElement>(root, '#exam')!;
      const tokenEl = qs<HTMLInputElement>(root, '#token')!;

      const runValidate = async (examCode: string, token: string) => {
        out.innerHTML = `<div class="center-col"><div class="spinner"></div><p>Validating…</p></div>`;
        try {
          showConfirm(examCode, token, await validateExam(examCode, token));
        } catch (e) {
          showError(e);
        }
      };

      /** Fill the fields from a scanned/pasted link, and validate when complete. */
      const applyLink = (text: string | null) => {
        if (!text) return; // cancelled
        const creds = parseTestifyLink(text);
        if (!creds) return toast('That is not a TESTIFY exam link', 'err');
        if (creds.examCode) examEl.value = creds.examCode;
        if (creds.token) tokenEl.value = creds.token;
        const examCode = examEl.value.trim();
        const token = tokenEl.value.trim();
        if (!examCode || !token) return toast(`Got the ${creds.token ? 'token' : 'exam code'} — fill in the rest`, 'err');
        void runValidate(examCode, token);
      };

      onClick(root, '[data-qr]', async () => applyLink(await scanQrDialog()));

      onClick(root, '[data-paste]', async () => {
        try {
          applyLink(await navigator.clipboard.readText());
        } catch {
          // Clipboard read denied/unsupported (Safari without a user gesture chain):
          // fall back to the field the teacher can paste into themselves.
          toast('Paste the link into the token box below', 'err');
          tokenEl.focus();
        }
      });

      // Pasting the whole exam URL into the token box is the obvious mistake —
      // split it into the two fields instead of failing with "invalid token".
      tokenEl.addEventListener('input', () => {
        const creds = /^https?:\/\//i.test(tokenEl.value.trim()) ? parseTestifyLink(tokenEl.value) : null;
        if (!creds) return;
        if (creds.examCode) examEl.value = creds.examCode;
        tokenEl.value = creds.token;
      });

      onClick(root, '[data-validate]', () => {
        const examCode = examEl.value.trim();
        const token = tokenEl.value.trim();
        if (!examCode || !token) return toast('Enter the exam code and token', 'err');
        void runValidate(examCode, token);
      });
    },
  };
}
